import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Camera,
  Dumbbell,
  X,
  Flame,
  ArrowLeft,
  User,
  Trophy,
  Zap,
  CheckCircle,
} from 'lucide-react';
import axios from 'axios';
import { API, resolvePhotoUrl } from '../utils/api';
import { LinkButton } from '../components/ui/link-button';

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];
const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const StudentCalendarPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/student/${userId}/calendar?month=${month}&year=${year}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, month, year]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const student = data?.student;

  return (
    <div className="min-h-screen bg-black p-4 pb-24" data-testid="student-calendar-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <LinkButton
          to="/admin/users-workouts"
          variant="ghost"
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Voltar</span>
        </LinkButton>

        {student && (
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-lime/20 to-neon-purple/20 border border-white/10 flex items-center justify-center">
              <User className="w-7 h-7 text-neon-lime" />
            </div>
            <div>
              <h1 className="font-primary font-bold text-xl text-white uppercase tracking-tight">
                {student.name}
              </h1>
              <p className="text-zinc-500 text-sm">{student.email}</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Stats Cards */}
      {data && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-4 gap-2 mb-5">
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Dumbbell className="w-4 h-4 text-neon-purple mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{data.total_workouts}</p>
            <p className="text-[10px] text-zinc-500">Treinos</p>
          </div>
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Camera className="w-4 h-4 text-neon-lime mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{data.total_photos}</p>
            <p className="text-[10px] text-zinc-500">Fotos</p>
          </div>
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Flame className="w-4 h-4 text-orange-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{data.streak}</p>
            <p className="text-[10px] text-zinc-500">Sequência</p>
          </div>
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Zap className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{student?.total_xp || 0}</p>
            <p className="text-[10px] text-zinc-500">XP Total</p>
          </div>
        </motion.div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4 px-1">
        <button onClick={prevMonth} data-testid="calendar-prev-month" className="w-9 h-9 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center hover:bg-zinc-800 active:scale-90 transition-all">
          <ChevronLeft className="w-5 h-5 text-zinc-400" />
        </button>
        <h2 className="font-bold text-lg text-white">
          {MONTHS_PT[month - 1]} {year}
        </h2>
        <button onClick={nextMonth} data-testid="calendar-next-month" className="w-9 h-9 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center hover:bg-zinc-800 active:scale-90 transition-all">
          <ChevronRight className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_PT.map(d => (
          <div key={d} className="text-center text-xs text-zinc-600 font-bold py-1">{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-neon-lime" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {data?.days?.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} className="aspect-square" />;

            const isToday = day.date === today;
            const hasWorkout = day.workouts_completed > 0;
            const hasPhoto = day.has_photo;

            return (
              <motion.button
                key={day.date}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.008 }}
                onClick={() => (hasWorkout || hasPhoto) ? setSelectedDay(day) : null}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${
                  hasPhoto
                    ? 'bg-neon-lime/20 border-2 border-neon-lime/50'
                    : hasWorkout
                    ? 'bg-neon-purple/15 border border-neon-purple/30'
                    : isToday
                    ? 'bg-zinc-900 border border-zinc-700'
                    : 'bg-zinc-950/50'
                } ${(hasWorkout || hasPhoto) ? 'cursor-pointer active:scale-90' : ''}`}
                data-testid={`cal-day-${day.date}`}
              >
                <span className={`text-sm font-bold ${
                  hasPhoto ? 'text-neon-lime' : hasWorkout ? 'text-neon-purple' : isToday ? 'text-white' : 'text-zinc-600'
                }`}>
                  {day.day}
                </span>
                {hasPhoto && <Camera className="w-3 h-3 text-neon-lime mt-0.5" />}
                {hasWorkout && !hasPhoto && <Flame className="w-3 h-3 text-neon-purple mt-0.5" />}
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 justify-center mt-4 text-xs text-zinc-500">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-neon-lime/20 border border-neon-lime/50" />
          <span>Com foto</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-neon-purple/15 border border-neon-purple/30" />
          <span>Treinou</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-zinc-950/50 border border-zinc-800" />
          <span>Sem treino</span>
        </div>
      </div>

      {/* Selected Day Detail Modal */}
      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-[60]"
            onClick={() => setSelectedDay(null)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              className="bg-zinc-900 border-t border-neon-lime/20 sm:border sm:rounded-2xl rounded-t-2xl p-5 pb-24 sm:pb-5 w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-white">
                  {new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
                <button onClick={() => setSelectedDay(null)} className="text-zinc-500 hover:text-white" data-testid="day-details-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Stats badges */}
              <div className="flex gap-3 mb-4">
                <div className="bg-neon-purple/10 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-neon-purple" />
                  <span className="text-sm text-white font-bold">{selectedDay.workouts_completed} treino(s)</span>
                </div>
                {selectedDay.has_photo && (
                  <div className="bg-neon-lime/10 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-neon-lime" />
                    <span className="text-sm text-white font-bold">{selectedDay.photos?.length} foto(s)</span>
                  </div>
                )}
              </div>

              {/* Workout details */}
              {selectedDay.completions?.length > 0 && (
                <div className="space-y-3 mb-4">
                  <h4 className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Detalhes dos Treinos</h4>
                  {selectedDay.completions.map((comp, idx) => (
                    <div key={`${comp.day_name}-${comp.completed_at}`} className="bg-zinc-950/80 border border-white/5 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-neon-lime" />
                        <span className="font-bold text-white text-sm">{comp.day_name}</span>
                      </div>
                      {(comp.exercises_completed > 0 || comp.total_exercises > 0) && (
                        <p className="text-xs text-zinc-400 ml-6">
                          {comp.exercises_completed}/{comp.total_exercises} exercícios concluídos
                        </p>
                      )}
                      {comp.completed_at && (
                        <p className="text-xs text-zinc-600 ml-6 mt-0.5">
                          {new Date(comp.completed_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Photos */}
              {selectedDay.photos?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Fotos</h4>
                  {selectedDay.photos.map(photo => (
                    <div key={photo.id} className="rounded-xl overflow-hidden border border-white/5">
                      <img
                        src={resolvePhotoUrl(photo.photo_url)}
                        alt="Treino"
                        className="w-full h-64 object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudentCalendarPage;
